import React, { useEffect } from "react"

import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { DrawSVGPlugin } from "gsap/DrawSVGPlugin"
import { MotionPathPlugin } from "gsap/MotionPathPlugin"

const TempStyleBlock = () => {
  return (
    <style suppressHydrationWarning>
      {`
        .info-graphic {
          padding: 5rem 0;
          display: flex;
          justify-content: center;
          flex-direction: column;
          align-items: center;
          transform: scale(1.3);
          padding-bottom: 10rem;
        }
        @media (min-width: 550px) {
          .info-graphic {
            transform: scale(1.4);
          }
        }
        @media (min-width: 768px) {
          .info-graphic {
            transform: scale(1.3);
          }
        }
        @media (min-width: 1024px) {
          .info-graphic {
            transform: scale(1.2);
          }
        }
        @media (min-width: 1920px) {
          .info-graphic {
            transform: scale(1);
          }
        }

        .info-graphic-wrapper {
          width: 100vw;
        }

        @media (min-width: 1280px) {
          .info-graphic-wrapper {
            width: 75vw;
          }
        }


        .svg-container {
          display: block;
          position: relative;
          width: 100%;
        }
        .svg-container:before {
          content: "";
          display: block;
          padding-top: 71%;
        }

        .svg-content {
          display: inline-block;
          position: absolute;
          top: 0;
          left: 0;
          transform-origin: 50% 50%;
        }

        .space-center-item {
          transform-origin: 50% 50%;
          width: 200px;
          height: 200px;
        }
        .space-center-item .opaque-circle {
          border-radius: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 0rem;
          width: 100%;
          height: 100%;
        }
        .space-center-item .opaque-circle img {
          width: 100%;
        }

        .constellation-grid {
          stroke: #4c94a6;
          fill: none;
        }
        .constellation-grid-path {
          transform-origin: 50% 50%;
        }

        .constellation-label {
          font-family: "Inter";
          font-weight: 100;
          font-size: 1.2rem;
          fill: #fff;
          opacity: 0;
        }
        .constellation-labels {
          transform-origin: 50% 50%;
          transform-box: view-box;
        }
        .constellation-decor {
          opacity: 0;
          transform-origin: 50% 50%;
        }
        .constellation-third .constellation-label-1-g {
          transform-origin: 50% 50%;
          transform: rotateZ(145deg);
        }
        .constellation-third .constellation-label-2-g {
          transform-origin: 50% 50%;
          transform: rotateZ(270deg);
        }
        .constellation-third .constellation-label-3-g {
          transform-origin: 50% 50%;
          transform: rotateZ(35deg);
        }

        .constellation-fifth .constellation-label-1-g {
          transform-origin: 50% 50%;
          transform: rotateZ(93deg);
        }
        .constellation-fifth .constellation-label-2-g {
          transform-origin: 50% 50%;
          transform: rotateZ(164deg);
        }
        .constellation-fifth .constellation-label-3-g {
          transform-origin: 50% 50%;
          transform: rotateZ(234deg);
        }
        .constellation-fifth .constellation-label-4-g {
          transform-origin: 50% 50%;
          transform: rotateZ(307deg);
        }
        .constellation-fifth .constellation-label-5-g {
          transform-origin: 50% 50%;
          transform: rotateZ(18deg);
        }

      `}
    </style>
  )
}


const Constellation = ({ block }) => {
  useEffect(() => {
    var clientWidth = (window.innerWidth > 0) ? window.innerWidth : window.screen.width
    window.setTimeout(function () {
      gsap.registerPlugin(ScrollTrigger, DrawSVGPlugin, MotionPathPlugin)
      let tl = gsap.timeline()
      let graphicHeight = document.querySelector('.info-graphic').offsetHeight
      let orbitItemsMovementEnding = Math.floor(graphicHeight * 0.5).toString() + "px center";
      let decorationMovementEnding = Math.floor(graphicHeight * 2.0).toString() + "px center";

      let panningDistance = clientWidth / 2
      let panningStart = (block.constellation_part_count === "third" ? "top 20px" : "top 80px")

      if (clientWidth <= 768) {
        let zoom_tl = gsap.timeline()
        .to(".svg-content", { duration: 200, scale: 2.0, transformOrigin: "50% 50%", force3D: false }, "zoom-in")
        .to(".svg-content", { duration: 200, translateX: -panningDistance, transformOrigin: "50% 50%", force3D: false }, "pan-east")
        .to(".svg-content", { duration: 200, translateX: panningDistance, transformOrigin: "50% 50%", force3D: false }, "pan-west")
        .to(".svg-content", { duration: 200, scale: 1.0, translateX: 0, translateY: 0, transformOrigin: "50% 50%", force3D: false }, "reset-pan-zoom-out");
        ScrollTrigger.create({
          animation: zoom_tl,
          trigger: ".info-graphic",
          pin: true,
          start: () => panningStart,
          end: () => "bottom top",
          scrub: 0.5,
          toggleActions: "play none none reset",
          invalidateOnRefresh: true,
        });

      }

      tl.fromTo(".space-center-item", { opacity: 0.6, scale: 0.1, transformOrigin: "50% 50%"}, { opacity: 1, scale: 1, ease: "elastic(1.25, 1)", transformOrigin: "50% 50%", scrollTrigger: {
        trigger: ".svg-container",
        start: "-220px center",
        end: decorationMovementEnding,
        toggleActions: "play none none reset",
        scrub: 1.4,
      }}, 0);

      tl.fromTo(".constellation-labels", { rotateZ: "45deg", transformOrigin: "50% 50%"}, { rotateZ: "0deg", transformOrigin: "50% 50%", scrollTrigger: {
        trigger: ".svg-container",
        start: "-220px center",
        end: orbitItemsMovementEnding,
        toggleActions: "play none none reset",
        scrub: 1.4,
      }}, 0);

      tl.fromTo(".constellation-label", { opacity: 0 }, { opacity: 1, scrollTrigger: {
        trigger: ".svg-container",
        start: "-220px center",
        end: orbitItemsMovementEnding,
        toggleActions: "play none none reset",
        scrub: 1.4,
      }}, 0);


      if (block.constellation_part_count) {
        tl.fromTo(".constellation-grid-path", { drawSVG: "0%", strokeWidth: "2px" }, {
          drawSVG: "100%", strokeWidth: "2px", scrollTrigger: {
            trigger: ".svg-container",
            start: "100px center",
            end: decorationMovementEnding,
            toggleActions: "play none none reset",
            scrub: 1.4,
          }
        }, 0);
      }

      tl.to(".constellation-third #constellation-decor-1", {opacity: 1, motionPath: { path: ".decor-path", align:".decor-path", start: 0.0, end: -0.08, alignOrigin: [0.5, 0.5]}, scrollTrigger: {
        trigger: ".svg-container",
        start: "-20px center",
        end: orbitItemsMovementEnding,
        toggleActions: "play none none reset",
        scrub: 1.1,
      }}, 0);

      tl.to(".constellation-third #constellation-decor-2", {opacity: 1, motionPath: { path: ".decor-path", align:".decor-path", start: 0.3, end: 0.25, alignOrigin: [0.5, 0.5]}, scrollTrigger: {
        trigger: ".svg-container",
        start: "-20px center",
        end: orbitItemsMovementEnding,
        toggleActions: "play none none reset",
        scrub: 1.1,
      }}, 0);

      tl.to(".constellation-third #constellation-decor-3", {opacity: 1, motionPath: { path: ".decor-path", align:".decor-path", start: 0.7, end: 0.6, alignOrigin: [0.5, 0.5]}, scrollTrigger: {
        trigger: ".svg-container",
        start: "-20px center",
        end: orbitItemsMovementEnding,
        toggleActions: "play none none reset",
        scrub: 1.1,
      }}, 0);


      tl.to(".constellation-fifth #constellation-decor-1", {opacity: 1, motionPath: { path: ".decor-path", align:".decor-path", start: 0.0, end: -0.25, alignOrigin: [0.5, 0.5]}, scrollTrigger: {
        trigger: ".svg-container",
        start: "-20px center",
        end: orbitItemsMovementEnding,
        toggleActions: "play none none reset",
        scrub: 1.1,
      }}, 0);

      tl.to(".constellation-fifth #constellation-decor-2", {opacity: 1, motionPath: { path: ".decor-path", align:".decor-path", start: 0.1, end: -0.05, alignOrigin: [0.5, 0.5]}, scrollTrigger: {
        trigger: ".svg-container",
        start: "-20px center",
        end: orbitItemsMovementEnding,
        toggleActions: "play none none reset",
        scrub: 1.1,
      }}, 0);

      tl.to(".constellation-fifth #constellation-decor-3", {opacity: 1, motionPath: { path: ".decor-path", align:".decor-path", start: 0.3, end: 0.15, alignOrigin: [0.5, 0.5]}, scrollTrigger: {
        trigger: ".svg-container",
        start: "-20px center",
        end: orbitItemsMovementEnding,
        toggleActions: "play none none reset",
        scrub: 1.1,
      }}, 0);

      tl.to(".constellation-fifth #constellation-decor-4", {opacity: 1, motionPath: { path: ".decor-path", align:".decor-path", start: 0.4, end: 0.35, alignOrigin: [0.5, 0.5]}, scrollTrigger: {
        trigger: ".svg-container",
        start: "-20px center",
        end: orbitItemsMovementEnding,
        toggleActions: "play none none reset",
        scrub: 1.1,
      }}, 0);

      tl.to(".constellation-fifth #constellation-decor-5", {opacity: 1, motionPath: { path: ".decor-path", align:".decor-path", start: 0.7, end: 0.55, alignOrigin: [0.5, 0.5]}, scrollTrigger: {
        trigger: ".svg-container",
        start: "-20px center",
        end: orbitItemsMovementEnding,
        toggleActions: "play none none reset",
        scrub: 1.1,
      }}, 0);

    }, 1000)
  }, [])

  return (
    <div className="info-graphic">
      <TempStyleBlock />
      <div className="info-graphic-wrapper">
          <div className="svg-container">
            <svg className="svg-content" viewBox="0 0 1400 1000" xmlns="http://www.w3.org/2000/svg">

              <defs>
                <symbol id="mm-circle" viewBox="0 0 500 500" xmlns="http://www.w3.org/2000/svg"><circle cx="250" cy="250" r="246" /></symbol>
              </defs>


              <g className={`constellation-${block.constellation_part_count}`}>
                {block.constellation_part_count === "third" && (
                  <>
                    <g className="constellation-grid constellation-grid-third">
                      <path className="constellation-grid-path" d="M 833.966 559.248 L 833.512 560.198 C 811.179 606.966 763.562 639.112 708.306 639.112 C 653.936 639.112 606.969 607.989 584.194 562.428 L 583.748 561.536 L 395.103 655.415 L 395.57 656.317 C 411.52 687.143 432.378 715.72 457.656 740.966 C 521.929 805.08 607.504 840.509 698.36 840.509 C 789.216 840.509 874.791 805.08 939.064 740.966 C 965.44 714.622 987.002 684.657 1003.203 652.281 L 1003.636 651.417 Z M 583.065 560.132 L 582.649 559.248 C 574.256 541.409 569.606 521.612 569.606 500.64 C 569.606 463.58 584.156 430.055 607.959 405.137 C 631.762 380.22 664.542 364.159 701.211 362.346 L 702.15 362.299 L 704.39 160.843 L 703.393 160.829 C 701.719 160.805 700.038 160.793 698.36 160.793 C 607.515 160.793 521.929 196.222 457.656 260.336 C 393.439 324.504 357.951 409.941 357.951 500.651 C 357.951 554.446 370.432 606.437 393.947 653.139 L 394.394 654.028 Z M 1004.325 650.024 L 1004.782 649.084 C 1026.997 603.441 1038.769 552.894 1038.769 500.651 C 1038.769 409.941 1003.281 324.504 939.064 260.336 C 876.776 198.2 794.464 163.036 706.762 160.894 L 705.749 160.87 L 703.513 362.246 L 704.551 362.218 C 705.8 362.184 707.048 362.167 708.306 362.167 C 746.619 362.167 781.167 377.641 806.337 402.77 C 831.506 427.899 847.005 462.39 847.005 500.64 C 847.005 520.73 842.738 539.736 834.999 556.988 L 834.62 557.833 Z M 805.373 403.728 C 780.559 378.955 746.098 363.522 708.306 363.522 C 670.514 363.522 636.053 378.955 611.239 403.728 C 586.425 428.502 570.967 462.908 570.967 500.64 C 570.967 538.371 586.425 572.778 611.239 597.552 C 636.053 622.325 670.514 637.758 708.306 637.758 C 746.098 637.758 780.559 622.325 805.373 597.552 C 830.186 572.778 845.645 538.371 845.645 500.64 C 845.645 462.908 830.186 428.502 805.373 403.728 Z M 698.36 942.848 C 638.527 942.848 580.581 931.178 525.953 908.095 C 473.174 885.822 425.854 853.97 385.161 813.329 C 344.453 772.7 312.55 725.459 290.241 672.765 C 267.119 618.215 255.431 560.375 255.431 500.64 C 255.431 440.904 267.12 383.054 290.241 328.515 C 312.55 275.821 344.453 228.579 385.161 187.951 C 425.854 147.31 473.174 115.458 525.953 93.184 C 580.581 70.101 638.527 58.432 698.36 58.432 C 758.193 58.432 816.139 70.101 870.767 93.184 C 923.546 115.458 970.866 147.31 1011.559 187.951 C 1052.267 228.579 1084.17 275.821 1106.479 328.515 C 1129.601 383.065 1141.289 440.904 1141.289 500.64 C 1141.289 560.375 1129.6 618.226 1106.479 672.765 C 1084.17 725.459 1052.267 772.7 1011.559 813.329 C 970.866 853.97 923.546 885.822 870.767 908.095 C 816.128 931.178 758.193 942.848 698.36 942.848 Z M 705.512 59.842 L 704.515 59.828 C 702.466 59.8 700.413 59.786 698.36 59.786 C 638.788 59.786 580.891 71.464 526.486 94.429 C 473.939 116.626 426.661 148.455 386.132 188.906 C 345.616 229.369 313.735 276.57 291.501 329.032 C 268.501 383.349 256.803 441.153 256.803 500.629 C 256.803 560.104 268.501 617.908 291.501 672.226 C 295.226 681.016 299.226 689.672 303.483 698.156 L 303.93 699.047 L 393.183 654.63 L 392.731 653.733 C 369.064 606.786 356.591 554.731 356.591 500.64 C 356.591 409.419 392.056 323.839 456.695 259.37 C 521.27 194.846 606.99 159.438 698.36 159.438 C 700.043 159.438 701.721 159.451 703.402 159.474 L 704.405 159.488 Z M 304.635 700.436 L 305.096 701.337 C 326.046 742.236 353.245 779.517 386.133 812.352 C 426.672 852.802 473.939 884.631 526.486 906.829 C 580.891 929.793 638.788 941.471 698.36 941.471 C 757.932 941.471 815.829 929.793 870.234 906.829 C 922.781 884.631 970.058 852.813 1010.588 812.352 C 1043.678 779.296 1071.021 741.751 1092.029 700.542 L 1092.471 699.674 L 1004.83 652.066 L 1004.367 652.99 C 988.094 685.504 966.528 715.465 940.025 741.909 C 875.45 806.433 789.719 841.841 698.36 841.841 C 607.001 841.841 521.27 806.433 456.696 741.909 C 431.262 716.526 410.374 687.907 394.343 656.893 L 393.891 656.018 Z M 1093.169 698.29 L 1093.632 697.364 C 1097.742 689.134 1101.61 680.742 1105.219 672.226 C 1128.219 617.908 1139.918 560.104 1139.918 500.629 C 1139.918 441.153 1128.219 383.349 1105.219 329.032 C 1082.985 276.57 1051.115 229.369 1010.587 188.906 C 970.047 148.455 922.781 116.626 870.234 94.429 C 818.73 72.689 764.092 61.07 707.881 59.886 L 706.871 59.865 L 705.765 159.516 L 706.752 159.539 C 794.977 161.611 877.431 196.828 940.024 259.37 C 1004.653 323.839 1040.129 409.419 1040.129 500.64 C 1040.129 553.229 1028.343 603.89 1005.936 649.813 L 1005.517 650.673 Z" />
                    </g>
                    <g className="constellation-labels">
                      {block.constellation_item_text_1 && (
                        <g className="constellation-label-1-g">
                          <text className="constellation-label constellation-label-1" dy="-30">
                            <textPath xlinkHref="#textcurve-cw" startOffset="50%" textAnchor="middle">
                              {block.constellation_item_text_1}
                            </textPath>
                          </text>
                        </g>
                      )}
                      {block.constellation_item_text_2 && (
                        <g className="constellation-label-2-g">
                          <text className="constellation-label constellation-label-2" dy="50">
                            <textPath xlinkHref="#textcurve-ccw" startOffset="50%" textAnchor="middle">
                              {block.constellation_item_text_2}
                            </textPath>
                          </text>
                        </g>
                      )}
                      {block.constellation_item_text_3 && (
                        <g className="constellation-label-3-g">
                          <text className="constellation-label constellation-label-3" dy="-30">
                            <textPath xlinkHref="#textcurve-cw" startOffset="50%" textAnchor="middle">
                              {block.constellation_item_text_3}
                            </textPath>
                          </text>
                        </g>
                      )}
                    </g>
                    {block.constellation_item_text_1 && (
                      <>
                        <g className="constellation-decor" id="constellation-decor-1">
                          <polygon points="1022.04 455.67 987.1 450.69 978.74 405.29 950.04 410.82 949.85 409.84 979.54 404.12 987.96 449.8 1020.53 454.44 995.98 387.14 996.6 357.56 989.96 326.5 990.93 326.29 997.6 357.46 997.6 357.52 996.98 386.97" style={{ fill: "rgb(76, 148, 166)" }}/>
                          <rect x="975.395" y="395.362" transform="matrix(0.701232, -0.712933, 0.712933, 0.701232, 12.8967, 822.531006)" width="24.739" height="1" style={{ fill: "rgb(76, 148, 166)" }}/>
                          <circle cx="950.32" cy="410.2" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="997.06" cy="387.15" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="1020.48" cy="454.8" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="986.59" cy="449.13" r="2.58" style={{ fill: "#fff" }}/>
                          <circle cx="979.74" cy="404.5" r="2.58" style={{ fill: "#fff" }}/>
                          <circle cx="997.09" cy="357.17" r="2.58" style={{ fill: "#fff" }}/>
                          <circle cx="990.37" cy="325.73" r="2.58" style={{ fill: "#fff" }}/>
                        </g>
                      </>
                    )}
                    {block.constellation_item_text_2 && (
                      <>
                        <g className="constellation-decor" id="constellation-decor-2">
                          <title>constellation-decor-3-2</title>
                          <polygon points="837.81 700.24 710.28 714.8 710.17 713.81 830.86 700.02 747.66 685.3 747.83 684.32" style={{ fill: "rgb(76, 148, 166)" }}/>
                          <rect x="849.237" y="690.347" transform="matrix(0.431827, -0.901956, 0.901956, 0.431827, -155.343307, 1168.452393)" width="1" height="34.278" style={{ fill: "rgb(76, 148, 166)" }}/>
                          <circle cx="713.23" cy="714.12" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="745.78" cy="710.75" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="748.74" cy="685.56" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="834.14" cy="699.96" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="866.23" cy="716.15" r="3.69" style={{ fill: "#fff" }}/>
                        </g>
                      </>
                    )}
                    {block.constellation_item_text_3 && (
                      <>
                        <g className="constellation-decor" id="constellation-decor-3">
                          <title>constellation-decor-3-3</title>
                          <polygon points="636.76 488.83 632.72 456.66 632.8 456.51 650.85 423.05 650.75 392.76 612.47 377.5 565.74 391.19 567.07 417.63 566.07 417.68 564.7 390.45 565.08 390.34 612.52 376.45 612.68 376.51 651.75 392.08 651.85 423.3 651.79 423.41 633.75 456.85 637.76 488.7" style={{ fill: "rgb(76, 148, 166)" }}/>
                          <rect x="601.366" y="339.053" transform="matrix(0.854291, -0.519795, 0.519795, 0.854291, -99.1716, 365.254486)" width="1" height="40.9" style={{ fill: "rgb(76, 148, 166)" }}/>
                          <circle cx="612.49" cy="376.97" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="633.21" cy="455.99" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="565.22" cy="390.82" r="2.22" style={{ fill: "#fff" }}/>
                          <circle cx="566.57" cy="417.65" r="2.22" style={{ fill: "#fff" }}/>
                          <circle cx="591.29" cy="341.56" r="2.22" style={{ fill: "#fff" }}/>
                          <circle cx="651.12" cy="392.59" r="2.22" style={{ fill: "#fff" }}/>
                          <circle cx="651.35" cy="423.18" r="2.22" style={{ fill: "#fff" }}/>
                          <circle cx="637.26" cy="488.76" r="2.22" style={{ fill: "#fff" }}/>
                        </g>
                      </>
                    )}
                  </>
                )}
                {block.constellation_part_count === "fifth" && (
                  <>
                    <g className="constellation-grid constellation-grid-fifth">
                      <path className="constellation-grid-path" d="M 706.378 842.068 L 705.356 842.082 C 703.691 842.105 702.028 842.118 700.36 842.118 C 609.001 842.118 523.298 806.626 458.696 742.024 C 420.124 703.452 391.948 657.399 375.477 607.008 L 375.173 606.076 L 279.758 635.204 L 280.071 636.172 C 284.004 648.331 288.488 660.369 293.502 672.224 C 315.735 724.769 347.599 772.054 388.133 812.576 C 428.666 853.099 475.939 884.974 528.486 907.207 C 582.89 930.209 640.788 941.906 700.36 941.906 C 701.782 941.906 703.206 941.899 704.625 941.886 L 705.608 941.877 Z M 374.756 604.783 L 374.451 603.82 C 363.995 570.77 358.591 536.02 358.591 500.36 C 358.591 408.99 394.056 323.271 458.696 258.696 C 476.51 240.878 495.903 225.303 516.628 212.042 L 517.478 211.498 L 462.902 127.958 L 462.067 128.494 C 435.51 145.542 410.761 165.491 388.133 188.121 C 347.617 228.661 315.735 275.928 293.502 328.475 C 270.5 382.879 258.803 440.777 258.803 500.349 C 258.803 545.799 265.612 590.293 279.045 632.96 L 279.343 633.906 Z M 463.827 127.369 L 518.629 210.769 L 519.458 210.25 C 573.221 176.557 635.338 158.591 700.36 158.591 C 777.932 158.591 851.401 184.153 911.381 231.456 L 912.185 232.091 L 972.078 152.232 L 971.304 151.629 C 941.024 128.045 907.831 108.55 872.235 93.49 C 817.83 70.489 759.932 58.792 700.36 58.792 C 640.788 58.792 582.89 70.489 528.485 93.49 C 506.24 102.9 484.916 114.047 464.688 126.826 Z M 913.25 232.938 L 914.003 233.543 C 923.731 241.364 933.073 249.743 942.024 258.696 C 1006.652 323.271 1042.129 408.99 1042.129 500.36 C 1042.129 541.103 1035.077 580.673 1021.502 617.831 L 1021.161 618.764 L 1114.594 653.628 L 1114.94 652.685 C 1132.834 604.044 1141.917 552.846 1141.917 500.349 C 1141.917 440.777 1130.22 382.879 1107.219 328.474 C 1084.985 275.928 1053.114 228.65 1012.587 188.121 C 1000.313 175.852 987.409 164.361 973.953 153.709 L 973.148 153.072 Z M 1020.688 620.038 L 1020.337 620.972 C 1003.428 665.925 977.015 707.055 942.024 742.024 C 879.421 804.678 796.938 839.953 708.707 842.018 L 707.739 842.041 L 706.969 941.859 L 707.994 941.842 C 764.872 940.878 820.156 929.226 872.235 907.207 C 924.781 884.974 972.059 853.103 1012.587 812.576 C 1053.103 772.036 1084.985 724.769 1107.219 672.224 C 1109.513 666.799 1111.697 661.332 1113.767 655.842 L 1114.121 654.903 Z M 911.363 233.187 L 910.596 232.583 C 850.901 185.559 777.514 159.962 700.36 159.962 C 635.732 159.962 573.722 177.924 520.245 211.372 L 519.379 211.914 L 633.053 385.212 L 633.89 384.657 C 655.852 370.083 682.038 361.661 710.306 361.661 C 741.385 361.661 769.969 371.843 793.133 389.17 L 793.932 389.768 Z M 518.23 212.641 L 517.397 213.174 C 496.774 226.373 477.388 241.94 459.657 259.668 C 395.439 323.939 359.951 409.515 359.951 500.371 C 359.951 535.843 365.362 570.562 375.756 603.442 L 376.054 604.386 L 578.204 542.682 L 577.902 541.721 C 573.798 528.63 571.607 514.785 571.607 500.36 C 571.607 453.195 595.064 411.637 631.128 386.534 L 631.926 385.979 Z M 376.47 605.681 L 376.788 606.65 C 393.195 656.73 421.32 702.717 459.657 741.075 C 523.928 805.292 609.504 840.78 700.36 840.78 C 702.044 840.78 703.73 840.768 705.41 840.744 L 706.388 840.73 L 707.942 639.042 L 706.958 639.019 C 677.174 638.337 649.832 628.239 627.494 611.561 C 605.157 594.884 588.054 571.795 578.936 544.887 L 578.625 543.969 Z M 795.011 390.596 L 795.782 391.199 C 828.265 416.633 849.005 456.01 849.005 500.36 C 849.005 517.882 845.771 534.567 839.818 550.02 L 839.455 550.963 L 1019.888 618.289 L 1020.233 617.343 C 1033.714 580.391 1040.769 540.88 1040.769 500.371 C 1040.769 409.515 1005.281 323.939 941.063 259.668 C 932.183 250.788 922.879 242.443 913.234 234.683 L 912.428 234.034 Z M 838.952 552.221 L 838.584 553.114 C 828.185 578.366 810.611 599.768 788.325 614.985 C 766.036 630.203 739.265 639.059 710.306 639.059 C 710.302 639.059 709.306 639.059 709.303 639.059 L 707.749 840.704 L 708.781 840.679 C 796.486 838.529 878.805 803.334 941.063 741.075 C 975.851 706.287 1002.225 665.199 1019.063 620.501 L 1019.416 619.563 Z M 700.36 943.288 C 640.527 943.288 582.582 931.6 527.953 908.479 C 475.174 886.17 427.855 854.267 387.161 813.559 C 346.453 772.865 314.551 725.546 292.241 672.766 C 269.12 618.127 257.432 560.193 257.432 500.36 C 257.432 440.527 269.12 382.582 292.241 327.954 C 314.551 275.174 346.453 227.855 387.161 187.161 C 427.855 146.453 475.174 114.551 527.954 92.241 C 582.582 69.12 640.527 57.432 700.36 57.432 C 760.193 57.432 818.138 69.12 872.767 92.241 C 909.222 107.653 943.073 127.649 973.957 151.97 L 973.986 151.992 L 973.986 151.993 L 974.733 152.583 C 988.274 163.294 1001.214 174.815 1013.559 187.161 C 1054.267 227.855 1086.17 275.174 1108.479 327.954 C 1131.6 382.593 1143.288 440.527 1143.288 500.36 C 1143.288 560.193 1131.6 618.138 1108.479 672.767 C 1086.17 725.546 1054.267 772.865 1013.559 813.559 C 972.865 854.267 925.546 886.17 872.766 908.479 C 818.127 931.6 760.193 943.288 700.36 943.288 Z M 807.373 403.293 C 782.56 378.479 748.098 363.021 710.306 363.021 C 672.513 363.021 638.052 378.479 613.238 403.293 C 588.425 428.106 572.967 462.568 572.967 500.36 C 572.967 538.152 588.425 572.614 613.238 597.427 C 638.052 622.241 672.513 637.699 710.306 637.699 C 748.098 637.699 782.56 622.241 807.373 597.427 C 832.187 572.614 847.645 538.152 847.645 500.36 C 847.645 462.568 832.187 428.106 807.373 403.293 Z"/>
                    </g>
                    <g className="constellation-labels">
                      {block.constellation_item_text_1 && (
                        <g className="constellation-label-1-g">
                          <text className="constellation-label constellation-label-1" dy="-30">
                            <textPath xlinkHref="#textcurve-cw" startOffset="50%" textAnchor="middle">
                              {block.constellation_item_text_1}
                            </textPath>
                          </text>
                        </g>
                      )}
                      {block.constellation_item_text_2 && (
                        <g className="constellation-label-2-g">
                          <text className="constellation-label constellation-label-2" dy="-30">
                            <textPath xlinkHref="#textcurve-cw" startOffset="50%" textAnchor="middle">
                              {block.constellation_item_text_2}
                            </textPath>
                          </text>
                        </g>
                      )}
                      {block.constellation_item_text_3 && (
                        <g className="constellation-label-3-g">
                          <text className="constellation-label constellation-label-3" dy="50">
                            <textPath xlinkHref="#textcurve-ccw" startOffset="50%" textAnchor="middle">
                              {block.constellation_item_text_3}
                            </textPath>
                          </text>
                        </g>
                      )}
                      {block.constellation_item_text_4 && (
                        <g className="constellation-label-4-g">
                          <text className="constellation-label constellation-label-4" dy="50">
                            <textPath xlinkHref="#textcurve-ccw" startOffset="50%" textAnchor="middle">
                              {block.constellation_item_text_4}
                            </textPath>
                          </text>
                        </g>
                      )}
                      {block.constellation_item_text_5 && (
                        <g className="constellation-label-5-g">
                          <text className="constellation-label constellation-label-5" dy="-30">
                            <textPath xlinkHref="#textcurve-cw" startOffset="50%" textAnchor="middle">
                              {block.constellation_item_text_5}
                            </textPath>
                          </text>
                        </g>
                      )}
                    </g>
                    {block.constellation_item_text_1 && (
                      <>
                        <g className="constellation-decor" id="constellation-decor-1">
                          <polygon points="749.19 298.89 749.05 297.9 803.96 290.29 804.03 290.3 836.43 294.48 837.34 267.99 828.55 254.27 795.92 250.06 730.01 266.24 729.77 265.27 795.86 249.04 795.95 249.05 829.14 253.34 838.35 267.72 838.35 267.87 837.39 295.62 803.96 291.3" style={{ fill: "rgb(76, 148, 166)" }}/>
                          <rect x="849.548" y="287.593" transform="matrix(0.514113, -0.857722, 0.857722, 0.514113, 153.196106, 876.30658)" width="1" height="30.669" style={{ fill: "rgb(76, 148, 166)" }}/>
                          <circle cx="795.89" cy="249.55" r="2.58" style={{ fill: "#fff" }}/>
                          <circle cx="836.91" cy="295.05" r="2.58" style={{ fill: "#fff" }}/>
                          <circle cx="837.93" cy="269.04" r="2.58" style={{ fill: "#fff" }}/>
                          <circle cx="864.52" cy="311.72" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="828.95" cy="253.63" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="804.19" cy="290.97" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="748.36" cy="299.97" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="730.48" cy="265.34" r="3.69" style={{ fill: "#fff" }}/>
                        </g>
                      </>
                    )}
                    {block.constellation_item_text_2 && (
                      <>
                        <g className="constellation-decor" id="constellation-decor-2">
                          <polygon points="1022.04 455.67 987.1 450.69 978.74 405.29 950.04 410.82 949.85 409.84 979.54 404.12 987.96 449.8 1020.53 454.44 995.98 387.14 996.6 357.56 989.96 326.5 990.93 326.29 997.6 357.46 997.6 357.52 996.98 386.97" style={{ fill: "rgb(76, 148, 166)" }}/>
                          <rect x="975.395" y="395.362" transform="matrix(0.701232, -0.712933, 0.712933, 0.701232, 12.8967, 822.531006)" width="24.739" height="1" style={{ fill: "rgb(76, 148, 166)" }}/>
                          <circle cx="950.32" cy="410.2" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="997.06" cy="387.15" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="1020.48" cy="454.8" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="986.59" cy="449.13" r="2.58" style={{ fill: "#fff" }}/>
                          <circle cx="979.74" cy="404.5" r="2.58" style={{ fill: "#fff" }}/>
                          <circle cx="997.09" cy="357.17" r="2.58" style={{ fill: "#fff" }}/>
                          <circle cx="990.37" cy="325.73" r="2.58" style={{ fill: "#fff" }} />
                        </g>
                      </>
                    )}
                    {block.constellation_item_text_3 && (
                      <>
                        <g className="constellation-decor" id="constellation-decor-3">
                          <polygon points="875.25 720.85 874.96 719.89 917.78 706.92 927.38 691.64 940.58 691.2 952.08 686.1 977.03 644.81 969.39 633.78 970.21 633.21 978.22 644.77 978.05 645.05 952.77 686.88 940.71 692.2 927.95 692.63 918.43 707.76 918.25 707.82" style={{ fill: "rgb(76, 148, 166)" }}/>
                          <polygon points="951.95 686.64 941.54 653.24 928.9 631.02 928.9 613.95 929.9 613.95 929.9 630.75 942.48 652.89 952.9 686.34" style={{ fill: "rgb(76, 148, 166)" }}/>
                          <circle cx="968.75" cy="631.71" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="942.31" cy="653.55" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="875.1" cy="720.37" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="940.78" cy="691.09" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="952.97" cy="686.25" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="976.76" cy="644.91" r="2.22" style={{ fill: "#fff" }}/>
                          <circle cx="927.67" cy="692.14" r="2.22" style={{ fill: "#fff" }}/>
                          <circle cx="929.4" cy="613.95" r="2.22" style={{ fill: "#fff" }}/>
                          <circle cx="929.4" cy="630.95" r="2.22" style={{ fill: "#fff" }}/>
                          <circle cx="917.67" cy="707.14" r="2.22" style={{ fill: "#fff" }}/>
                        </g>
                      </>
                    )}
                    {block.constellation_item_text_4 && (
                      <>
                        <g className="constellation-decor" id="constellation-decor-4">
                          <polygon points="837.81 700.24 710.28 714.8 710.17 713.81 830.86 700.02 747.66 685.3 747.83 684.32" style={{ fill: "rgb(76, 148, 166)" }}/>
                          <rect x="849.237" y="690.347" transform="matrix(0.431827, -0.901956, 0.901956, 0.431827, -155.343307, 1168.452393)" width="1" height="34.278" style={{ fill: "rgb(76, 148, 166)" }}/>
                          <circle cx="713.23" cy="714.12" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="745.78" cy="710.75" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="748.74" cy="685.56" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="834.14" cy="699.96" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="866.23" cy="716.15" r="3.69" style={{ fill: "#fff" }}/>
                        </g>
                      </>
                    )}
                    {block.constellation_item_text_5 && (
                      <>
                        <g className="constellation-decor" id="constellation-decor-5">
                          <polygon points="636.76 488.83 632.72 456.66 632.8 456.51 650.85 423.05 650.75 392.76 612.47 377.5 565.74 391.19 567.07 417.63 566.07 417.68 564.7 390.45 565.08 390.34 612.52 376.45 612.68 376.51 651.75 392.08 651.85 423.3 651.79 423.41 633.75 456.85 637.76 488.7" style={{ fill: "rgb(76, 148, 166)" }}/>
                          <rect x="601.366" y="339.053" transform="matrix(0.854291, -0.519795, 0.519795, 0.854291, -99.1716, 365.254486)" width="1" height="40.9" style={{ fill: "rgb(76, 148, 166)" }}/>
                          <circle cx="612.49" cy="376.97" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="633.21" cy="455.99" r="3.69" style={{ fill: "#fff" }}/>
                          <circle cx="565.22" cy="390.82" r="2.22" style={{ fill: "#fff" }}/>
                          <circle cx="566.57" cy="417.65" r="2.22" style={{ fill: "#fff" }}/>
                          <circle cx="591.29" cy="341.56" r="2.22" style={{ fill: "#fff" }}/>
                          <circle cx="651.12" cy="392.59" r="2.22" style={{ fill: "#fff" }}/>
                          <circle cx="651.35" cy="423.18" r="2.22" style={{ fill: "#fff" }}/>
                          <circle cx="637.26" cy="488.76" r="2.22" style={{ fill: "#fff" }}/>
                        </g>
                      </>
                    )}
                  </>
                )}

              </g>

              <g className="orbit-tools">
                <title>orbit-tools</title>
                <path id="textcurve-cw" style={{ fill: "none", stroke: " none" }} d="M 1050 500 C 1050 693.3 893.299 850 700 850 C 506.701 850 350 693.3 350 500 C 350 306.7 506.701 150 700 150 C 893.299 150 1050 306.7 1050 500 Z" />
                <path id="textcurve-ccw" style={{ fill: "none", stroke: " none" }} d="M 1050 500 C 1050 306.7 893.299 150 700 150 C 506.701 150 350 306.7 350 500 C 350 693.3 506.701 850 700 850 C 893.299 850 1050 693.3 1050 500 Z"/>
                <path className="decor-path" style={{ fill: "none", stroke: " none" }} d="M 950 500 C 950 638.072 838.071 750 700 750 C 561.929 750 450 638.072 450 500 C 450 361.928 561.929 250 700 250 C 838.071 250 950 361.928 950 500 Z" />
              </g>

              { block.center_logo && block.center_logo.filename && (
                <g className="space-center-item">
                  <foreignObject width="200" height="200" x="600" y="400">
                    <div className="opaque-circle">
                      <img src={block.center_logo.filename} alt={block.center_logo.alt} />
                    </div>
                  </foreignObject>
                </g>
              )}
            </svg>
          </div>
      </div>
    </div>
  )
}

export default Constellation
